import { useDrawerDestinationPageNavigator } from "#use-drawer-destination-page-navigator";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { NotificationDot } from "@-/dev.improve.haniverse.components.ui.notification-dot";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { LocalAccount } from "@-/dev.improve.haniverse.lib.auth.client";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useChangelogPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.changelog.navigator";
import { useOnboardingUnlockedDestinations } from "@-/dev.improve.haniverse.lib.onboarding";
import type { OnboardingDestination } from "@-/dev.improve.haniverse.lib.onboarding.state";
import {
  isLearnMapPathname,
  useMapPageNavigator,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.map.navigator";
import { useAccountSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.account.navigator";
import { ProfileAvatar } from "@-/dev.improve.haniverse.components.ui.profile-card";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { HanStreakFlame } from "@-/dev.improve.haniverse.assets.icons";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useLearningStatsReviewHistoryQueryOptions } from "@-/dev.improve.haniverse.lib.learning-stats";
import { rankProgressForXp, useXpQueryOptions } from "@-/dev.improve.haniverse.lib.xp";
import { useSettingsIndexPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.navigator";
import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { Pressable, ScrollView, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import {
  createAnimatedComponent,
  type SharedValue,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { type ReactNode, useEffect, useState } from "react";
import { usePathname } from "@-/com.npmjs.expo-router";
import { useIsNavAccountDrawerOpen, useNavAccountDrawerContent } from "#nav-account-drawer-context";
import { NavAccountDrawerWallet } from "#nav-account-drawer-wallet";
import { NavAccountDrawerCardsLearnedToday } from "#nav-account-drawer-cards-learned-today";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";

const drawerScrollContentContainerStyle = { paddingBottom: 16, paddingTop: 0 };

const AnimatedView = createAnimatedComponent(View);

/** How long a newly unlocked row waits before popping in: the panel slides in first. */
const DESTINATION_REVEAL_DELAY_MS = 300;
/** The strip's own pop (`useOnboardingDestinationReveal`), so a destination arrives alike in both. */
const DESTINATION_REVEAL_SPRING = { damping: 9, mass: 0.6, stiffness: 180 } as const;

type DrawerDestinationKey = OnboardingDestination | "cookbook";

type DrawerDestinationRow = { icon: IconName; key: DrawerDestinationKey; label: string };

/** The study group, in drawer order. The map stands in for Learn while the onboarding presents it. */
const STUDY_ROWS = [
  { icon: "MdSchool", key: "learn", label: "Learn" },
  { icon: "MdMap", key: "map", label: "Map" },
  { icon: "MdInsights", key: "stats", label: "Stats" },
  { icon: "GoStack", key: "decks", label: "Deck" },
] as const satisfies readonly DrawerDestinationRow[];

const DISCOVER_ROWS = [
  { icon: "MdGridView", key: "browse", label: "Browse" },
  { icon: "MdSearch", key: "search", label: "Search" },
  { icon: "MdMenuBook", key: "cookbook", label: "Cookbook" },
] as const satisfies readonly DrawerDestinationRow[];

/**
 * Whether the drawer lists a destination. While a new learner's onboarding is under way it lists
 * what the strip does (`useOnboardingUnlockedDestinations`): the map alone at first (Learn's own
 * entry, presented as the map), then each destination as the tour hands it out. Learn's map has no
 * entry of its own otherwise, and experimental Cookbook waits for the learner's own strip too.
 */
function isDrawerDestinationListed(
  key: DrawerDestinationKey,
  unlocked: readonly OnboardingDestination[] | null,
  isCookbookEnabled: boolean,
): boolean {
  switch (key) {
    case "cookbook": {
      return isCookbookEnabled && unlocked === null;
    }
    case "map": {
      return unlocked?.includes("map") === true;
    }
    default: {
      return unlocked === null || unlocked.includes(key);
    }
  }
}

/** The name the user gave their account, else its email (an anonymous account has no name). */
function accountDisplayName(account: LocalAccount): string {
  const name = account.user?.name.trim() ?? "";
  if (name.length > 0) {
    return name;
  }
  // A local profile entered without an email (Get Started → Continue without an account).
  return account.email.length > 0 ? account.email : "Guest";
}

function AccountWarningBadge({ label }: { label: string }) {
  return (
    <View className="mt-1 flex-row items-center gap-1 self-start rounded-full bg-amber-500/15 px-2 py-0.5">
      <Icon className="text-amber-700 dark:text-amber-300" name="LuAlertTriangle" size={12} />
      <Text className="text-xs font-medium text-amber-700 dark:text-amber-300">{label}</Text>
    </View>
  );
}

/** How the profile card reads, per account status. Only a signed-in account has a name to show. */
const profileDetailsByStatus = {
  sessionExpired: {
    accessibilityLabel: (email: string) => `Account settings for ${email}`,
    testID: "nav-profile-drawer-log-in",
    warning: "Log in again to sync",
  },
  signedIn: {
    accessibilityLabel: (displayName: string) => `Account settings for ${displayName}`,
    testID: "nav-profile-drawer-user-details",
    warning: null,
  },
  unverified: {
    accessibilityLabel: (email: string) => `Account settings for ${email}`,
    testID: "nav-profile-drawer-verify-email",
    warning: "Email needs verification",
  },
} as const satisfies Record<
  LocalAccount["status"],
  {
    accessibilityLabel: (displayName: string) => string;
    testID: string;
    warning: string | null;
  }
>;

/**
 * The account panel's header, in the native drawer and in web's account popover. It shows the
 * account's name, else its email. An account that is not signed in shows why with a warning
 * badge. Every account opens its profile page. The body holds the wallet and today's new cards
 * against the Daily New Cards limit. Both close the panel as they navigate.
 */
export function NavAccountDrawerProfileSurface({ account }: { account: LocalAccount }) {
  const dailyNewCardsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  const accountSettingsPageNavigator = useAccountSettingsPageNavigator();
  const { closeDrawer } = useNavAccountDrawerContent();
  const onOpenAccount = () => {
    closeDrawer();
    accountSettingsPageNavigator.push.mutate();
  };
  const onOpenDailyNewCards = () => {
    closeDrawer();
    dailyNewCardsPageNavigator.push.mutate();
  };
  const displayName = accountDisplayName(account);
  const details = profileDetailsByStatus[account.status];
  const username = account.user?.username?.trim();
  const isOpen = useIsNavAccountDrawerOpen();
  const xpQuery = useQuery({ ...useXpQueryOptions(), enabled: isOpen, throwOnError: false });
  const streakQuery = useQuery({
    ...useLearningStatsReviewHistoryQueryOptions(),
    enabled: isOpen,
    throwOnError: false,
  });
  const progress = xpQuery.data === undefined ? undefined : rankProgressForXp(xpQuery.data);
  const streak = streakQuery.data?.currentStreak;
  const streakLabel =
    streak === undefined
      ? streakQuery.isError
        ? "Streak unavailable"
        : "Loading streak…"
      : `${streak.toLocaleString()} ${streak === 1 ? "day" : "days"} streak`;

  return (
    <Surface testID="nav-profile-drawer-profile-surface">
      <SurfaceHeader asChild className="gap-3 py-3 active:bg-accent">
        <Pressable
          accessibilityLabel={details.accessibilityLabel(displayName)}
          accessibilityRole="button"
          onPress={onOpenAccount}
          testID={details.testID}
        >
          <View className="flex-row items-center gap-2">
            <View
              className="size-12 shrink-0 overflow-hidden rounded-xs border border-border/60"
              testID="nav-profile-drawer-avatar"
            >
              <ProfileAvatar
                shape="square"
                user={account.user ?? { email: account.email, name: displayName }}
              />
            </View>
            {/* Name and username identify the learner: masked in session replays (the avatar
                masks itself). One wrapper per line keeps the column gap. */}
            <View className="h-12 min-w-0 flex-1 justify-center gap-0.5">
              <PostHogMaskView>
                <Text className="text-base font-bold" numberOfLines={1}>
                  {displayName}
                </Text>
              </PostHogMaskView>
              {username ? (
                <PostHogMaskView>
                  <Text
                    className="text-xs text-muted-foreground"
                    numberOfLines={1}
                    testID="nav-profile-drawer-username"
                  >
                    @{username}
                  </Text>
                </PostHogMaskView>
              ) : null}
            </View>
            <View className="h-12 shrink-0 items-end justify-center gap-0.5">
              <View
                accessibilityLabel={streakLabel}
                accessible
                className="flex-row items-center gap-1"
                testID="nav-profile-drawer-streak"
              >
                <HanStreakFlame accessible={false} size={20} />
                <Text className="text-base font-semibold tabular-nums">
                  {streak === undefined ? "—" : streak.toLocaleString()}
                </Text>
              </View>
              <Text className="text-xs font-semibold" testID="nav-profile-drawer-rank">
                {progress
                  ? `Rank ${progress.rank}`
                  : xpQuery.isError
                    ? "Rank unavailable"
                    : "Loading rank…"}
              </Text>
            </View>
          </View>
          <View className="flex-row items-center gap-3">
            <View className="min-w-0 flex-1">
              <Progress
                accessibilityLabel={
                  progress ? `Progress to Rank ${progress.rank + 1}` : "Rank progress"
                }
                accessibilityState={{ busy: xpQuery.isPending }}
                className="h-1.5 bg-primary/15"
                indicatorClassName="bg-primary"
                value={progress ? (100 * progress.xpIntoRank) / progress.xpPerRank : 0}
              />
            </View>
            <Text className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {xpQuery.data === undefined ? "— XP" : `${xpQuery.data.toLocaleString()} XP`}
            </Text>
          </View>
          {details.warning !== null ? <AccountWarningBadge label={details.warning} /> : null}
        </Pressable>
      </SurfaceHeader>
      <SurfaceContent className="gap-3 p-3">
        <NavAccountDrawerWallet />
        <NavAccountDrawerCardsLearnedToday onOpenDailyNewCards={onOpenDailyNewCards} />
      </SurfaceContent>
    </Surface>
  );
}

type DrawerActionProps = {
  /** Shows a trailing dot; source it from `useNavAccountDrawer().notifications` so the trigger lights too. */
  hasNotification?: boolean;
  icon: IconName;
  label: string;
  onPress: () => void;
  selected?: boolean;
  testID: string;
};

export function NavAccountDrawerAction({
  hasNotification = false,
  icon,
  label,
  onPress,
  revealProgress,
  selected = false,
  testID,
}: DrawerActionProps & {
  /** 0 → 1 as the row first pops in (`RevealedDrawerAction`); left out, the row is simply there. */
  revealProgress?: SharedValue<number>;
}) {
  const iconNode = <Icon className="text-foreground" name={icon} size={20} />;
  const labelNode = <Text className="flex-1 text-sm font-medium">{label}</Text>;
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityValue={hasNotification ? { text: "New" } : undefined}
      className={`min-h-11 flex-row items-center gap-3 rounded-xs px-2.25 py-2 hover:bg-accent active:bg-accent ${selected ? "bg-accent" : ""}`}
      onPress={onPress}
      testID={testID}
    >
      {revealProgress === undefined ? (
        <>
          {iconNode}
          {labelNode}
        </>
      ) : (
        <DrawerActionReveal icon={iconNode} label={labelNode} progress={revealProgress} />
      )}
      {hasNotification ? <NotificationDot testID={`${testID}-notification-dot`} /> : null}
    </Pressable>
  );
}

/** The row's icon pops into place as the strip's tab does, and its label fades in with it. */
function DrawerActionReveal({
  icon,
  label,
  progress,
}: {
  icon: ReactNode;
  label: ReactNode;
  progress: SharedValue<number>;
}) {
  const iconStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, Math.max(0, progress.get())),
    transform: [{ scale: Math.max(0, progress.get()) }],
  }));
  const labelStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, Math.max(0, progress.get())),
  }));
  return (
    <>
      <AnimatedView style={iconStyle}>{icon}</AnimatedView>
      <AnimatedView className="flex-1" style={labelStyle}>
        {label}
      </AnimatedView>
    </>
  );
}

/**
 * A destination the onboarding unlocked after the panel first rendered, so the learner has not seen
 * it here yet: it pops in the first time the drawer opens on it, as it popped into the strip. Only
 * these rows follow the drawer's open state; the rest stay out of the opening commit.
 */
function RevealedDrawerAction(props: DrawerActionProps) {
  const isOpen = useIsNavAccountDrawerOpen();
  const reduceMotion = useReducedMotion();
  const revealProgress = useSharedValue(reduceMotion ? 1 : 0);
  useEffect(() => {
    // Once revealed it rests at 1, and a later opening springs from 1 to 1: nothing moves.
    if (isOpen) {
      revealProgress.set(
        withDelay(DESTINATION_REVEAL_DELAY_MS, withSpring(1, DESTINATION_REVEAL_SPRING)),
      );
    }
  }, [isOpen, revealProgress]);
  return <NavAccountDrawerAction {...props} revealProgress={revealProgress} />;
}


/**
 * The account panel's tabs, in the native drawer and in web's account popover: Learn, Stats and
 * Deck; Browse, Search and (when enabled) Cookbook; then Settings and Changelog. Each closes the
 * panel as it navigates, and the row for the current page is selected.
 */
export function NavAccountDrawerDestinations() {
  const mapPageNavigator = useMapPageNavigator();
  const changelogPageNavigator = useChangelogPageNavigator();
  const settingsPageNavigator = useSettingsIndexPageNavigator();
  const { closeDrawer, notifications } = useNavAccountDrawerContent();
  const destinationNavigator = useDrawerDestinationPageNavigator();
  const pathname = usePathname();
  // Cookbook is experimental; off until Admin settings enables it.
  const [isCookbookEnabled] = useDeviceSetting("isCookbookEnabled");
  const unlockedDestinations = useOnboardingUnlockedDestinations();
  const listed = (rows: readonly DrawerDestinationRow[]) =>
    rows.filter((row) =>
      isDrawerDestinationListed(row.key, unlockedDestinations, isCookbookEnabled),
    );
  const studyRows = listed(STUDY_ROWS);
  const discoverRows = listed(DISCOVER_ROWS);
  // What the panel listed when it first rendered; a destination unlocked since pops in.
  const [initiallyListed] = useState(
    () => new Set([...studyRows, ...discoverRows].map((row) => row.key)),
  );
  const openDestination = (
    destination: Parameters<typeof destinationNavigator.navigate.mutate>[0],
  ) => {
    closeDrawer();
    destinationNavigator.navigate.mutate(destination);
  };

  const openMap = () => {
    closeDrawer();
    mapPageNavigator.navigate.mutate();
  };

  const renderDestinationRow = (row: DrawerDestinationRow) => {
    const Action = initiallyListed.has(row.key) ? NavAccountDrawerAction : RevealedDrawerAction;
    return (
      <Action
        icon={row.icon}
        key={row.key}
        label={row.label}
        onPress={() => (row.key === "map" ? openMap() : openDestination(row.key))}
        selected={
          row.key === "map" ? isLearnMapPathname(pathname) : pathname.startsWith(`/${row.key}`)
        }
        testID={`nav-profile-drawer-${row.key}`}
      />
    );
  };

  const openSettings = () => {
    closeDrawer();
    settingsPageNavigator.push.mutate();
  };

  const openChangelog = () => {
    closeDrawer();
    changelogPageNavigator.push.mutate();
  };

  return (
    <>
      <View testID="nav-profile-drawer-study-group">
        {studyRows.map((row) => renderDestinationRow(row))}
      </View>
      <View className="my-2 h-px bg-border" />
      {discoverRows.length > 0 ? (
        <>
          <View testID="nav-profile-drawer-discover-group">
            {discoverRows.map((row) => renderDestinationRow(row))}
          </View>
          <View className="my-2 h-px bg-border" />
        </>
      ) : null}
      <View testID="nav-profile-drawer-settings-group">
        <NavAccountDrawerAction
          icon="MdSettings"
          label="Settings"
          onPress={openSettings}
          selected={pathname.startsWith("/settings")}
          testID="nav-profile-drawer-settings"
        />
        <NavAccountDrawerAction
          hasNotification={notifications.changelog}
          icon="MdHistory"
          label="Changelog"
          onPress={openChangelog}
          selected={pathname.startsWith("/changelog")}
          testID="nav-profile-drawer-changelog"
        />
      </View>
    </>
  );
}

/**
 * Custom drawer content for the account drawer.
 *
 * Rendered by `react-native-drawer-layout` inside its own drawer view, so this component owns
 * only the panel's contents — never the panel surface, its animation, or the scrim. The
 * opaque background lives on the drawer's `drawerStyle` (see `NavAccountDrawer`); the
 * scroll view paints the same color so an overscroll bounce can't reveal the scene beneath.
 *
 * A plain `ScrollView` plus explicit safe-area insets replace `DrawerContentScrollView`, which
 * reads the navigator's insets from drawer navigation context that this non-navigator drawer does
 * not provide. Help lives beside the panel in the fixed shell controls.
 *
 * Its profile surface and tabs are shared with web's account popover (`NavAccountDrawerPopover`).
 */
export function NavAccountDrawerContent({ account }: { account: LocalAccount }) {
  const safeAreaInsets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();

  return (
    <View
      className="bg-background px-2.25"
      style={{
        height: windowHeight,
        paddingBottom: safeAreaInsets.bottom + 9,
        // Landscape: keep rows clear of the notch/Dynamic Island on the right edge.
        paddingRight: safeAreaInsets.right + 9,
        paddingTop: safeAreaInsets.top,
      }}
    >
      <ScrollView
        className="flex-1 bg-background"
        contentContainerStyle={drawerScrollContentContainerStyle}
        showsVerticalScrollIndicator={false}
        testID="nav-profile-drawer-content"
      >
        <View className="bg-background">
          <View className="pt-3">
            <NavAccountDrawerProfileSurface account={account} />
          </View>
          <View className="my-2 h-px bg-border" />
          <NavAccountDrawerDestinations />
        </View>
      </ScrollView>
    </View>
  );
}
